import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import AIChatWidget from './components/AIChatWidget';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import TutorsPage from './pages/TutorsPage';
import TutorProfilePage from './pages/TutorProfilePage';
import BookingPage from './pages/BookingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import StudentDashboard from './pages/StudentDashboard';
import TutorDashboard from './pages/TutorDashboard';
import AdminDashboard from './pages/AdminDashboard';
import MessagesPage from './pages/MessagesPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import ParentConsentPage from './pages/ParentConsentPage';
import PricingPage from './pages/PricingPage';

// Legal Pages
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ConsentPolicyPage from './pages/ConsentPolicyPage';
import SessionPolicyPage from './pages/SessionPolicyPage';
import AcceptableUsePage from './pages/AcceptableUsePage';
import CheckoutPage from './pages/CheckoutPage';
import FAQPage from './pages/FAQPage';

// Tutor Submission Page (special link access)
import TutorSubmissionPage from './pages/TutorSubmissionPage';

// Tutor Hours Entry Page (student-specific link)
import TutorHoursEntryPage from './pages/TutorHoursEntryPage';

// Assignment-based Hours Entry Page (secure tutor assignment link)
import AssignmentHoursEntryPage from './pages/AssignmentHoursEntryPage';

// Password Reset Pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Email Verification Page
import VerifyEmailPage from './pages/VerifyEmailPage';

// Tutor Onboarding Pages
import TutorAgreementPage from './pages/TutorAgreementPage';
import TutorDocumentsPage from './pages/TutorDocumentsPage';

// Student Agreement Page
import StudentAgreementPage from './pages/StudentAgreementPage';

// SEO Landing Pages
import MathTutorTorontoPage from './pages/MathTutorTorontoPage';

// Invoice PDF Test Page (Admin only)
import InvoicePDFTestPage from './pages/InvoicePDFTestPage';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/tutors" element={<TutorsPage />} />
      <Route path="/tutors/:tutorId" element={<TutorProfilePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/consent/:consentToken" element={<ParentConsentPage />} />
      
      {/* SEO Landing Pages */}
      <Route path="/math-tutor-toronto" element={<MathTutorTorontoPage />} />
      
      {/* Tutor Hours Entry (Public - student-specific link) */}
      <Route path="/tutor-hours/:token" element={<TutorHoursEntryPage />} />
      
      {/* Assignment-based Tutor Hours Entry (Public - secure assignment link) */}
      <Route path="/tutor-submit/:token" element={<AssignmentHoursEntryPage />} />
      
      {/* Email Verification Page */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Tutor Onboarding Routes (Protected - tutors only) */}
      <Route path="/tutor/agreement" element={
        <ProtectedRoute allowedRoles={['tutor']}>
          <TutorAgreementPage />
        </ProtectedRoute>
      } />
      <Route path="/tutor/documents" element={
        <ProtectedRoute allowedRoles={['tutor']}>
          <TutorDocumentsPage />
        </ProtectedRoute>
      } />
      
      {/* Student Agreement Route (Public - can be accessed during registration or after login) */}
      <Route path="/student/agreement" element={<StudentAgreementPage />} />
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/consent-policy" element={<ConsentPolicyPage />} />
      <Route path="/session-policy" element={<SessionPolicyPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/faq" element={<FAQPage />} />
      
      {/* Payment Routes */}
      <Route path="/checkout" element={
        <ProtectedRoute>
          <CheckoutPage />
        </ProtectedRoute>
      } />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancel" element={<PaymentCancelPage />} />
      
      {/* Protected Routes */}
      <Route path="/book/:tutorId" element={
        <ProtectedRoute>
          <BookingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/tutor-dashboard" element={
        <ProtectedRoute allowedRoles={['tutor']}>
          <TutorDashboard />
        </ProtectedRoute>
      } />
      
      {/* Admin route handles its own authentication */}
      <Route path="/admin" element={<AdminDashboard />} />
      
      {/* Invoice PDF Test Page - Admin only */}
      <Route path="/admin/invoice-test" element={<InvoicePDFTestPage />} />
      
      {/* Tutor Submission - accessible by link only, no auth */}
      <Route path="/become-a-tutor" element={<TutorSubmissionPage />} />
      
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/messages/:partnerId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Wrapper to show AI Chat on public pages only
function PublicAIChatWrapper() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Show AI chat on public pages and for non-admin users
  const publicPaths = ['/', '/tutors', '/contact', '/about', '/pricing', '/login', '/register', '/math-tutor-toronto'];
  const isPublicPage = publicPaths.some(path => location.pathname === path || location.pathname.startsWith('/tutor/'));
  
  // Also show for logged-in students
  const isStudent = isAuthenticated && user?.role === 'student';
  
  // Hide for admin and tutor dashboards
  const isAdminOrTutorDashboard = location.pathname === '/admin' || location.pathname === '/tutor-dashboard';
  
  if (isAdminOrTutorDashboard) return null;
  if (isPublicPage || isStudent) {
    return <AIChatWidget />;
  }
  
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="academic-watermark">
            <AppRouter />
            <PublicAIChatWrapper />
            <Toaster position="top-right" richColors />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
